import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import EditButton from "../../../../../components/EditButton";
import SaveButton from "../../../../../components/SaveButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import TextInput from "../../../../../components/TextInput";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import EmployeeBilingualForm from "../form/EmployeeBilingualForm";
import {
  addStateEmployeeBilingual,
  editRowIdEmployeeBilingual,
  editStateEmployeeBilingual,
  rowForEditEmployeeBilingual,
  savingEmployeeBilingual,
  selectedRowsStateEmployeeBilingual,
} from "./../atoms/atoms";
import { deleteEmployeeBilingual } from "../../../../../actions/Utilities/EmployeeBilingual";
import useEditEmployeeBilingual from "./../hooks/useEditEmployeeBilingual";
import useGetEmployeeBilingual from "./../hooks/useGetEmployeeBilingual";
import { IEmployeeBilingualDetails } from "./../types";

interface Prop {}

export const EmployeeBilingualTable: React.FC<Prop> = () => {
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_utilities_employeebilingual_add;
  const editPermission =
    PERMISSIONS.permission_utilities_employeebilingual_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_employeebilingual_delete;

  const [actionAddState, setActionAddState] = useRecoilState(
    addStateEmployeeBilingual
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateEmployeeBilingual
  );
  const [rowToEdit, setRowToEdit] = useRecoilState(rowForEditEmployeeBilingual);
  const [displayNote] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editRowId, setEditRowId] = useRecoilState(editRowIdEmployeeBilingual);
  const [hideColumn, sethideColumn] = useState(false);
  const [checkStatus, setCheckStatus] = useState(false);
  const [bilingualDataIsChange, setBilingualDataIsChange] = useState(false);
  const [hasEmployeeBilingual, setHasEmployeeBilingual] = useState(true);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [, setBilingualExistState] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateEmployeeBilingual
  );
  const [employeeBilingualList, setEmployeeBilingualList] = useState([]);
  const [newData, setNewData] = useState<IEmployeeBilingualDetails>();
  const [, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(savingEmployeeBilingual);
  const queryClient = useQueryClient();
  const [filterText, setFilterText] = useState("");
  const [employeeBilingualFilteredData, setEmployeeBilingualFilteredData] =
    useState([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);

  const {
    data: employeeBilingualData,
    isLoading: isFetching,
    isSuccess: employeeBilingualSuccess,
  } = useGetEmployeeBilingual();

  // Edit Employee Bilingual
  const {
    mutate: editEmployeeBilingual,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditEmployeeBilingual();

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  useEffect(() => {
    if (employeeBilingualSuccess && employeeBilingualData) {
      if (employeeBilingualData?.status === 200) {
        setEmployeeBilingualList(
          employeeBilingualData?.data.map((data) => ({ ...data }))
        );
      } else {
        toast.fire(
          "Server Error",
          "Cannot retrieve Employee Bilingual Data",
          "error"
        );
      }
      setEmployeeBilingualList([]);
    }
  }, [employeeBilingualData, employeeBilingualSuccess]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  //For filtered data update
  const [bilingualId, setBilingualId] = useState("");
  useEffect(() => {
    if (bilingualId?.includes(".")) {
      let bilingualIds = bilingualId.split(".");
      const bilingualIdsArr = bilingualIds.map((id) => Number(id));
      const filteredData = employeeBilingualData?.data?.filter((x) =>
        bilingualIdsArr.includes(x.bilingualId)
      );
      setEmployeeBilingualFilteredData(filteredData);
    } else {
      const filteredData = employeeBilingualData?.data?.filter(
        (x) => x.bilingualId === Number(bilingualId)
      );
      setEmployeeBilingualFilteredData(filteredData);
    }
  }, [bilingualId, employeeBilingualData]);

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setEmployeeBilingualFilteredData(result);
          setBilingualId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  useEffect(() => {
    if (filterText) {
      setEmployeeBilingualList(
        employeeBilingualData?.data.filter((x) =>
          x.bilingualName
            .toLowerCase()
            .includes(filterText.trim().toLowerCase())
        )
      );
    } else {
      setEmployeeBilingualList(employeeBilingualData?.data);
    }
  }, [filterText, employeeBilingualData?.data]);

  useEffect(() => {
    setBilingualExistState(false);
    setBilingualDataIsChange(false);
    setActionAddState(false);
    setActionEditState(false);
    setSelectedRows([]);
    setEditRowId([]);
  }, []);

  const handleDelete = () => {
    const ids = selectedRows.map((row) => row.bilingualId);
    setSelectedRows([]);
    setOpenModalDelete(false);
    deactEmployeeBilingual.mutate(ids);
  };

  const handleEdit = (rowDetails) => {
    setRowToEdit({ ...rowDetails, isActive: rowDetails.isActive });
    setActionEditState(true);
    sethideColumn(true);
    setBilingualDataIsChange(false);
    setEditRowId(rowDetails.bilingualId);
    setCheckStatus(rowDetails.isActive);
  };

  const handleEditFieldsChange = (SourceType, fieldName, row, inputvalue) => {
    setBilingualExistState(false);
    setBilingualDataIsChange(false);
    inputvalue = inputvalue.trim();
    if (SourceType === "employeeBilingual") {
      if (fieldName === "EmployeeBilingualName") {
        setBilingualDataIsChange(true);
        const result = employeeBilingualData?.data.filter(
          (item) =>
            item.bilingualName?.toLowerCase() === inputvalue?.toLowerCase()
        )[0];
        result !== undefined
          ? setBilingualExistState(true)
          : setBilingualExistState(false);
        newData!.bilingualName = inputvalue;
      }
      inputvalue === ""
        ? setHasEmployeeBilingual(false)
        : setHasEmployeeBilingual(true);
    }
  };
  const handleCheckBox = (e) => {
    const checked = e.target.checked;
    setBilingualDataIsChange(true);
    checked ? setCheckStatus(true) : setCheckStatus(false);
    newData!.isActive = checked;
  };

  //Save changes
  const handleSave = (newData) => {
    if (
      rowToEdit?.bilingualName === newData?.bilingualName &&
      rowToEdit?.isActive === newData?.isActive
    ) {
      setIsSaving(false);
      toast.fire(
        "Server Error",
        "Failed to Update Employee Bilingual! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetEmployeeBilingual");
      setOpenModalEdit(false);
    } else {
      editEmployeeBilingual(newData);
      setEditRowId([]);
      setSelectedRows([]);
      setBilingualExistState(false);
      setBilingualDataIsChange(false);
      setOpenModalEdit(false);
      setActionEditState(false);
      setDisabledButton(false);
      setEdit(false);
      sethideColumn(false);
    }
  };

  const handleCancel = () => {
    setActionEditState(false);
    setRowToEdit(undefined);
    setEditRowId([]);
    setBilingualDataIsChange(false);
    setBilingualExistState(false);
    setDisabledButton(false);
    setEdit(false);
    sethideColumn(false);
    queryClient.invalidateQueries("GetEmployeeBilingual");
  };

  const matColumns = [
    {
      title: "Employee Bilingual Name",
      field: "bilingualName",
      customSort: (a, b) =>
        a.bilingualName && b.bilingualName
          ? a.bilingualName.trim().localeCompare(b.bilingualName.trim())
          : a.bilingualName
          ? 0
          : -1,
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer">
            <TextInput
              placeholder="Employee Bilingual"
              defaultValue={rowData.bilingualName}
              onChange={(inputdata) => {
                handleEditFieldsChange(
                  "employeeBilingual",
                  "EmployeeBilingualName",
                  rowData,
                  inputdata.target.value
                );
              }}
              value={undefined}
            />
          </div>
        ) : (
          <span>{rowData.bilingualName}</span>
        );
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      hidden: hideColumn,
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.createdBy ? rowData.createdBy : rowData.createdBy}</>
        );
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.createdDate
              ? new Date(rowData.createdDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      hidden: hideColumn,
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.modifiedBy ? rowData.modifiedBy : rowData.modifiedBy}</>
        );
      },
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.modifiedDate
              ? new Date(rowData.modifiedDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData.bilingualId === editRowId ? (
          <div className="flex space-x-1 cursor-pointer">
            <input
              type="checkbox"
              className="flex items-center space-x-1 cursor-pointer mt-0.5"
              defaultChecked={rowData.isActive}
              onClick={(e) => {
                handleCheckBox(e);
              }}
            />
            <label className="items-center cursor-pointer text-green-600 text-xs">
              {checkStatus ? "Uncheck to Deactivate" : "Check to Activate"}
            </label>
          </div>
        ) : (
          <div>
            <div className="font-sans text-left mr-4 text-xs">
              {rowData.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      render: (rowData) => {
        return !userPermissions.includes(editPermission) ? (
          ""
        ) : rowData.bilingualId === editRowId ? (
          <div className="flex items-center place-content-center space-x-2 w-full mr-2">
            <SaveButton
              className={`flex items-center shadow-md text-xs font-semibold text-white  gap-x-1 rounded-full  bg-senary hover:bg-senary px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
            ${
              !hasEmployeeBilingual || !bilingualDataIsChange
                ? "opacity-50"
                : "opactiy-100"
            }`}
              disabled={!hasEmployeeBilingual || !bilingualDataIsChange}
              onClick={() => {
                setOpenModalEdit(true);
              }}
            />
            <CancelButton
              onClick={(event) => {
                event.preventDefault();
                handleCancel();
              }}
            />
          </div>
        ) : (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              disabled={disabledButton || selectedRows.length > 0}
              className={
                disabledButton || selectedRows.length > 0
                  ? "opacity-50 rounded-full items-center bg-quaternary  text-white hover:bg-hquaternary "
                  : "opacity-100 rounded-full items-center bg-quaternary  text-white hover:bg-hquaternary"
              }
              onClick={() => {
                setDisabledButton(true);
                setActionEditState(true);
                handleEdit(rowData);
                setNewData(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  //Deactivate Toast
  const deactEmployeeBilingual = useMutation(
    (ids: number[] | undefined) => deleteEmployeeBilingual(ids),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["GetEmployeeBilingual"]);
        toast.fire(
          "Success",
          "Employee Bilingual were deactivated successfully",
          "success"
        );
      },
      onError: () => {
        toast.fire(
          "Bulk deactivation of Employee Bilingual failed",
          "Server Error: Cannot deactivate Employee Bilingual",
          "error"
        );
      },
    }
  );

  //Edit Result
  if (editSuccess && editResult) {
    if (editResult && editResult.status === 400) {
      toast.fire("Failed", "Employee Bilingual Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
      queryClient.invalidateQueries("GetEmployeeBilingual");
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      toast.fire(
        "Success",
        "Employee Bilingual Updated Successfully.",
        "success"
      );
      queryClient.invalidateQueries("GetEmployeeBilingual");
      //searchRefetch();
      handleCancel();
      setRowToEdit(undefined);
      setActionEditState(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Employee Bilingual updated successfully, but " +
            editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowToEdit(undefined);
      } else {
        setIsSaving(false);
        setRowToEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Employee Bilingual! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetEmployeeBilingual");
      setActionEditState(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);

    toast.fire(
      "Server Error",
      "Failed to Update Employee Bilingual! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetEmployeeBilingual");
    setActionEditState(false);
  }

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Employee Bilinguals" : "Employee Bilingual"
    }?`}</div>
  );

  return (
    <>
      <div className="animate-fade-in-down p-4 bg-white">
        {!actionAddState && (
          <>
            <div className="flex justify-between text-xs items-center w-full">
              <TableFilters
                moduleDetails={"bilingualName"}
                textInputName={"EmployeeBilingualName"}
                columnDetails={"Employee Bilingual Name"}
                filterText={setFilterText}
                disabledButton={
                  actionEditState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <AddButton
                    buttonText="New Employee Bilingual"
                    onClick={() => setActionAddState(!actionAddState)}
                    disabled={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>
            <div className="">
              <MaterialTableTS
                data={
                  employeeBilingualFilteredData?.length > 0
                    ? employeeBilingualFilteredData
                    : employeeBilingualList
                }
                showTitle={true}
                columns={matColumns}
                isLoading={isFetching}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={
                  !actionAddState &&
                  !actionEditState &&
                  userPermissions.includes(deletePermission)
                }
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxModal
              ModalTitle="Update Employee Bilingual"
              qConfirmation=" Are you sure you want to update this?"
              isOpen={openModalEdit}
              onClose={() => {
                setOpenModalEdit(false);
                setIsSaving(false);
              }}
              onClickFunction={() => {
                handleSave(newData);
                setIsSaving(false);
              }}
              cancelButtonRef={undefined}
            />
            <ConfirmBoxDelete
              ModalTitle="Deactivate Employee Bilingual"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
        {actionAddState && (
          <div className="">
            <EmployeeBilingualForm
              isAdd={actionAddState}
              setIsAdd={setActionAddState}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeBilingualTable;
